'use client'

/**
 * EmbedViewCapture — records WHERE a widget was loaded (FRIDGE-356 step 4).
 *
 * One `embed_widget_view` PostHog event per widget load, carrying the asset
 * slug and the ORIGIN of `document.referrer` (scheme + host only — the
 * embedding page's own Referrer-Policy already reduces a cross-origin iframe's
 * referrer to its origin, and this never keeps more than that). No path, no
 * query, no personal data. That is enough for the card's success number:
 *   count(distinct properties.referrer_origin) where event = 'embed_widget_view'
 *
 * Self-previews (the "Embed this" card on fridge.com framing its own widget)
 * are skipped — a fridge.com referrer is not an embedding domain. Loads with
 * no referrer (a publisher sending `no-referrer`, or the widget opened
 * top-level) are recorded as `unknown` so the count of embedded loads is still
 * honest, with `embedded` saying whether the page sat inside a frame at all.
 *
 * PostHog is the site's existing analytics pattern (app/providers.tsx): it
 * initialises only on fridge.com production and, for consent-gated visitors,
 * only after Allow — before init `capture` is DROPPED by the SDK, so this
 * component inherits every consent rule the rest of the site follows.
 *
 * FRIDGE-807 (09/04/2026): this effect runs BEFORE the provider's — React
 * runs a child's mount effects first — so the capture used to reach the SDK
 * before `init` and was dropped on every framed load ("You must initialize
 * PostHog before calling posthog.capture"; 0 events ever recorded). It now
 * waits on the provider's loaded signal (lib/posthog-loaded.ts) and captures
 * the moment the client is ready.
 */
import { useEffect, useRef } from 'react'
import { usePostHog } from 'posthog-js/react'
import type { EmbedAssetSlug } from '@/lib/embed/assets'
import { whenPostHogLoaded } from '@/lib/posthog-loaded'

export function referrerOrigin(referrer: string): string {
  if (!referrer) return 'unknown'
  try {
    return new URL(referrer).origin
  } catch {
    return 'unknown'
  }
}

export function EmbedViewCapture({ asset }: { asset: EmbedAssetSlug }) {
  const posthog = usePostHog()
  // One view per mount. React's development Strict Mode re-runs mount effects
  // once (production never does); the ref survives that pass, so a dev load
  // records one view exactly as a production load would.
  const sent = useRef(false)

  useEffect(() => {
    if (!posthog || typeof window === 'undefined' || sent.current) return
    const origin = referrerOrigin(document.referrer)
    if (origin === window.location.origin) return // self-preview, not an embed
    let embedded = false
    try {
      embedded = window.self !== window.top
    } catch {
      embedded = true // cross-origin parent throws on access — that IS a frame
    }
    return whenPostHogLoaded(posthog, () => {
      if (sent.current) return
      sent.current = true
      posthog.capture('embed_widget_view', {
        asset,
        referrer_origin: origin,
        embedded,
      })
    })
  }, [posthog, asset])

  return null
}
